/**
 * SpendingAnalysisModal - AI-powered spending analysis from credit card data
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  PieChart,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Store,
  Repeat,
  ShoppingBag,
  Utensils,
  Car,
  Film,
  Fuel,
  Heart,
  Plane,
  Receipt,
  ShoppingCart,
  Wallet,
  MoreHorizontal,
  CreditCard,
  Info,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  BarChart3,
} from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';

// Category configuration
const CATEGORY_CONFIG = {
  food: { label: 'Yemek', icon: Utensils, color: '#fb923c' },
  groceries: { label: 'Market', icon: ShoppingCart, color: '#4ade80' },
  shopping: { label: 'Alışveriş', icon: ShoppingBag, color: '#f472b6' },
  transport: { label: 'Ulaşım', icon: Car, color: '#60a5fa' },
  fuel: { label: 'Akaryakıt', icon: Fuel, color: '#facc15' },
  entertainment: { label: 'Eğlence', icon: Film, color: '#c084fc' },
  subscriptions: { label: 'Abonelik', icon: Repeat, color: '#22d3ee' },
  bills: { label: 'Fatura', icon: Receipt, color: '#94a3b8' },
  health: { label: 'Sağlık', icon: Heart, color: '#f87171' },
  travel: { label: 'Seyahat', icon: Plane, color: '#818cf8' },
  fee: { label: 'Masraf', icon: Wallet, color: '#fbbf24' },
  cash_advance: { label: 'Nakit', icon: Wallet, color: '#fb7185' },
  other: { label: 'Diğer', icon: MoreHorizontal, color: '#6b7280' },
};

// Health score colors
const HEALTH_COLORS = {
  excellent: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  good: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  fair: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  poor: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  critical: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
};

// Priority colors
const PRIORITY_COLORS = {
  high: 'bg-red-500/20 text-red-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  low: 'bg-blue-500/20 text-blue-400',
};

// Insight type colors
const INSIGHT_COLORS = {
  warning: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: AlertTriangle, iconColor: 'text-yellow-400' },
  info: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: Info, iconColor: 'text-blue-400' },
  positive: { bg: 'bg-green-500/10', border: 'border-green-500/30', icon: TrendingUp, iconColor: 'text-green-400' },
  negative: { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: TrendingDown, iconColor: 'text-red-400' },
};

const SpendingAnalysisModal = () => {
  const {
    isSpendingAnalysisOpen,
    closeSpendingAnalysis,
    spendingAnalysis,
    spendingAnalysisUpdatedAt,
    isAnalyzingSpending,
    spendingAnalysisError,
    runSpendingAnalysis,
    creditCardTransactions,
    creditCardStatements,
    isLoadingCreditCards,
    isLoadingCachedAnalysis,
    creditCardPaymentMode,
    toggleCreditCardPaymentMode,
  } = useCashflowStore();

  // Calculate total minimum vs full balance from statements
  const paymentSummary = creditCardStatements.reduce((acc, stmt) => {
    const minPayment = parseFloat(stmt.minimum_payment) || 0;
    const fullBalance = parseFloat(stmt.total_balance) || 0;
    return {
      totalMinimum: acc.totalMinimum + minPayment,
      totalFull: acc.totalFull + fullBalance,
      statementCount: acc.statementCount + 1,
    };
  }, { totalMinimum: 0, totalFull: 0, statementCount: 0 });

  // Estimated interest if paying only minimum (rough estimate: remaining balance * monthly rate)
  // Average credit card interest rate in Turkey is around 4-5% monthly
  const MONTHLY_INTEREST_RATE = 0.045; // 4.5%
  const remainingAfterMinimum = paymentSummary.totalFull - paymentSummary.totalMinimum;
  const estimatedMonthlyInterest = remainingAfterMinimum * MONTHLY_INTEREST_RATE;

  // Selected month tab: 'all' or month string like '2025-11'
  const [selectedMonthTab, setSelectedMonthTab] = useState('all');
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  // Run analysis when modal opens if we have data and no cached analysis
  useEffect(() => {
    if (isSpendingAnalysisOpen && !spendingAnalysis && !isLoadingCachedAnalysis && creditCardTransactions.length > 0 && !isAnalyzingSpending) {
      runSpendingAnalysis();
    }
  }, [isSpendingAnalysisOpen, creditCardTransactions.length, isLoadingCachedAnalysis]);

  // Format last updated time
  const formatLastUpdated = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Reset selected tab when analysis changes
  useEffect(() => {
    if (spendingAnalysis?.monthlyBreakdown?.length > 0) {
      setExpandedMonths(new Set(spendingAnalysis.monthlyBreakdown.map(m => m.month)));
    }
  }, [spendingAnalysis]);

  const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const toggleMonthExpand = (month) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(month)) {
        next.delete(month);
      } else {
        next.add(month);
      }
      return next;
    });
  };

  if (!isSpendingAnalysisOpen) return null;

  const hasData = creditCardTransactions.length > 0;
  const analysis = spendingAnalysis;
  const healthColors = analysis?.healthScore >= 80 ? HEALTH_COLORS.excellent
    : analysis?.healthScore >= 60 ? HEALTH_COLORS.good
    : analysis?.healthScore >= 40 ? HEALTH_COLORS.fair
    : analysis?.healthScore >= 20 ? HEALTH_COLORS.poor
    : HEALTH_COLORS.critical;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={closeSpendingAnalysis}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl bg-gray-900 border border-gray-800 rounded-xl shadow-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
              <PieChart className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                Harcama Analizi
                <Sparkles className="w-4 h-4 text-pink-400" />
              </h2>
              <p className="text-sm text-gray-400">
                {creditCardTransactions.length} işlem
                {spendingAnalysisUpdatedAt && (
                  <span className="ml-2 text-gray-500">
                    • Son güncelleme: {formatLastUpdated(spendingAnalysisUpdatedAt)}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => runSpendingAnalysis(true)}
              disabled={isAnalyzingSpending || !hasData}
              className="flex items-center gap-2 px-3 py-2 text-pink-400 hover:text-pink-300 hover:bg-pink-500/10 rounded-lg transition-colors disabled:opacity-50"
              title="Analizi Yenile (Yeni veri ile)"
            >
              <RefreshCw className={`w-4 h-4 ${isAnalyzingSpending ? 'animate-spin' : ''}`} />
              <span className="text-sm hidden sm:inline">Yenile</span>
            </button>
            <button
              onClick={closeSpendingAnalysis}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoadingCreditCards || isLoadingCachedAnalysis ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="w-12 h-12 text-pink-400 animate-spin" />
              <p className="text-gray-400">
                {isLoadingCachedAnalysis ? 'Kayıtlı analiz yükleniyor...' : 'Kredi kartı verileri yükleniyor...'}
              </p>
            </div>
          ) : !hasData ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <CreditCard className="w-16 h-16 text-gray-600" />
              <p className="text-gray-400 text-center">
                Henüz kredi kartı ekstresi yüklenmemiş
              </p>
              <p className="text-sm text-gray-500 text-center">
                Harcama analizi için önce bir kredi kartı ekstresi yükleyin
              </p>
            </div>
          ) : isAnalyzingSpending ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="w-12 h-12 text-pink-400 animate-spin" />
              <p className="text-gray-400">Harcama verileri analiz ediliyor...</p>
              <p className="text-xs text-gray-500">Bu işlem birkaç saniye sürebilir</p>
            </div>
          ) : spendingAnalysisError ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <AlertTriangle className="w-12 h-12 text-red-400" />
              <p className="text-red-400">{spendingAnalysisError}</p>
              <button
                onClick={runSpendingAnalysis}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Tekrar Dene
              </button>
            </div>
          ) : analysis ? (
            <div className="space-y-6">
              {/* Health Score Card */}
              <div className={`${healthColors.bg} ${healthColors.border} border rounded-xl p-6`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Harcama Sağlığı</p>
                    <p className={`text-3xl font-bold ${healthColors.text}`}>
                      {analysis.healthScore}/100
                    </p>
                  </div>
                  <div className="w-24 h-24 relative">
                    <svg className="transform -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="8"
                        className="text-gray-700"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="8"
                        strokeDasharray={`${(analysis.healthScore || 0) * 2.51} 251`}
                        className={healthColors.text}
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-300 mt-4">{analysis.summary}</p>
              </div>

              {/* Payment Mode & Interest Warning */}
              {paymentSummary.statementCount > 0 && remainingAfterMinimum > 0 && (
                <div className={`rounded-xl p-4 ${
                  creditCardPaymentMode === 'minimum'
                    ? 'bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/30'
                    : 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/30'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className={`w-5 h-5 ${
                          creditCardPaymentMode === 'minimum' ? 'text-orange-400' : 'text-green-400'
                        }`} />
                        <h3 className="font-medium text-white">
                          {creditCardPaymentMode === 'minimum' ? 'Asgari Ödeme Modu' : 'Tam Borç Ödeme Modu'}
                        </h3>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-500">Asgari Ödeme</p>
                          <p className="text-lg font-semibold text-green-400">
                            {formatCurrency(paymentSummary.totalMinimum)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Toplam Borç</p>
                          <p className="text-lg font-semibold text-orange-400">
                            {formatCurrency(paymentSummary.totalFull)}
                          </p>
                        </div>
                      </div>

                      {creditCardPaymentMode === 'minimum' && (
                        <div className="p-2 bg-red-500/10 rounded-lg">
                          <p className="text-xs text-red-400 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Sadece asgari ödeme yaparsanız, kalan {formatCurrency(remainingAfterMinimum)} borç için
                            tahmini aylık <strong className="text-red-300">{formatCurrency(estimatedMonthlyInterest)}</strong> faiz ödersiniz.
                          </p>
                        </div>
                      )}

                      {creditCardPaymentMode === 'full' && (
                        <div className="p-2 bg-green-500/10 rounded-lg">
                          <p className="text-xs text-green-400 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            Tüm borcu ödeyerek aylık {formatCurrency(estimatedMonthlyInterest)} faizden kurtulursunuz!
                          </p>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={toggleCreditCardPaymentMode}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        creditCardPaymentMode === 'minimum'
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                      }`}
                    >
                      {creditCardPaymentMode === 'minimum' ? 'Tam Borç Göster' : 'Asgari Göster'}
                    </button>
                  </div>
                </div>
              )}

              {/* Monthly Breakdown - Collapsible sections for each month */}
              {analysis.monthlyBreakdown && analysis.monthlyBreakdown.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-medium text-white flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-pink-400" />
                    Aylık Kırılım
                  </h3>
                  {analysis.monthlyBreakdown.map((monthData, idx) => {
                    const isExpanded = expandedMonths.has(monthData.month);
                    const changeColor = monthData.changePercentage > 0 ? 'text-red-400' : monthData.changePercentage < 0 ? 'text-green-400' : 'text-gray-400';

                    return (
                      <div key={monthData.month} className="bg-gray-800/30 rounded-lg overflow-hidden">
                        {/* Month Header */}
                        <button
                          onClick={() => toggleMonthExpand(monthData.month)}
                          className="w-full p-4 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <div className="text-left">
                              <p className="font-medium text-white">{monthData.monthLabel}</p>
                              <p className="text-xs text-gray-500">{monthData.transactionCount} işlem</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-white">{formatCurrency(monthData.totalSpending)}</p>
                            {monthData.changePercentage !== null && monthData.changePercentage !== undefined && (
                              <p className={`text-xs flex items-center justify-end gap-1 ${changeColor}`}>
                                {monthData.changePercentage > 0 ? (
                                  <ArrowUpRight className="w-3 h-3" />
                                ) : monthData.changePercentage < 0 ? (
                                  <ArrowDownRight className="w-3 h-3" />
                                ) : null}
                                {monthData.changePercentage > 0 ? '+' : ''}{monthData.changePercentage?.toFixed(1)}%
                              </p>
                            )}
                          </div>
                        </button>

                        {/* Month Details */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="border-t border-gray-700/50"
                            >
                              <div className="p-4 space-y-4">
                                {/* Commentary */}
                                {monthData.commentary && (
                                  <p className="text-sm text-gray-300 bg-gray-800/50 p-3 rounded-lg">
                                    {monthData.commentary}
                                  </p>
                                )}

                                {/* Interest/Payment info */}
                                {(monthData.interestCharged > 0 || monthData.totalPayments > 0) && (
                                  <div className="flex gap-3 text-sm">
                                    {monthData.totalPayments > 0 && (
                                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded">
                                        Ödeme: {formatCurrency(monthData.totalPayments)}
                                      </span>
                                    )}
                                    {monthData.interestCharged > 0 && (
                                      <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded">
                                        Faiz: {formatCurrency(monthData.interestCharged)}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Category Breakdown for this month */}
                                {monthData.spendingByCategory && monthData.spendingByCategory.length > 0 && (
                                  <div>
                                    <h4 className="text-xs text-gray-500 uppercase mb-2">Kategori Dağılımı</h4>
                                    <div className="space-y-2">
                                      {monthData.spendingByCategory.slice(0, 5).map((cat, catIdx) => {
                                        const config = CATEGORY_CONFIG[cat.category] || CATEGORY_CONFIG.other;
                                        const Icon = config.icon;
                                        return (
                                          <div key={catIdx} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <Icon className="w-3 h-3" style={{ color: config.color }} />
                                              <span className="text-sm text-gray-300">{cat.categoryLabel || config.label}</span>
                                            </div>
                                            <div className="text-right">
                                              <span className="text-sm text-white">{formatCurrency(cat.amount)}</span>
                                              <span className="text-xs text-gray-500 ml-1">%{cat.percentage?.toFixed(0)}</span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Top Merchants for this month */}
                                {monthData.topMerchants && monthData.topMerchants.length > 0 && (
                                  <div>
                                    <h4 className="text-xs text-gray-500 uppercase mb-2">En Çok Harcanan Yerler</h4>
                                    <div className="grid grid-cols-2 gap-2">
                                      {monthData.topMerchants.slice(0, 4).map((merchant, mIdx) => {
                                        const config = CATEGORY_CONFIG[merchant.category] || CATEGORY_CONFIG.other;
                                        return (
                                          <div key={mIdx} className="flex items-center justify-between bg-gray-800/50 rounded p-2">
                                            <div className="flex items-center gap-1 min-w-0">
                                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: config.color }} />
                                              <span className="text-xs text-gray-300 truncate">{merchant.name}</span>
                                            </div>
                                            <span className="text-xs text-white ml-1">{formatCurrency(merchant.amount)}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Overall Category Breakdown (from overallAnalysis or legacy spendingByCategory) */}
              {(analysis.overallAnalysis?.spendingByCategory || analysis.spendingByCategory) && (
                <div className="bg-gray-800/30 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                    <PieChart className="w-4 h-4 text-pink-400" />
                    Genel Kategori Dağılımı
                  </h3>
                  <div className="space-y-3">
                    {(analysis.overallAnalysis?.spendingByCategory || analysis.spendingByCategory).map((cat, idx) => {
                      const config = CATEGORY_CONFIG[cat.category] || CATEGORY_CONFIG.other;
                      const Icon = config.icon;
                      return (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" style={{ color: config.color }} />
                              <span className="text-sm text-gray-300">{cat.categoryLabel || config.label}</span>
                              <span className="text-xs text-gray-500">({cat.count} işlem)</span>
                            </div>
                            <div className="text-right">
                              <span className="text-sm font-medium text-white">
                                {formatCurrency(cat.amount)}
                              </span>
                              <span className="text-xs text-gray-500 ml-2">
                                %{cat.percentage?.toFixed(1) || 0}
                              </span>
                            </div>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${cat.percentage || 0}%`,
                                backgroundColor: config.color,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Overall Top Merchants */}
              {(analysis.overallAnalysis?.topMerchants || analysis.topMerchants) && (
                <div className="bg-gray-800/30 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                    <Store className="w-4 h-4 text-pink-400" />
                    En Çok Harcanan Yerler (Tüm Dönem)
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {(analysis.overallAnalysis?.topMerchants || analysis.topMerchants).slice(0, 8).map((merchant, idx) => {
                      const config = CATEGORY_CONFIG[merchant.category] || CATEGORY_CONFIG.other;
                      return (
                        <div key={idx} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: config.color }}
                            />
                            <span className="text-sm text-gray-300 truncate">{merchant.name}</span>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className="text-sm font-medium text-white">{formatCurrency(merchant.amount)}</p>
                            <p className="text-xs text-gray-500">{merchant.count}x</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Monthly Trend */}
              {analysis.monthlyTrend && analysis.monthlyTrend.length > 0 && (
                <div className="bg-gray-800/30 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-pink-400" />
                    Aylık Trend
                  </h3>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {analysis.monthlyTrend.map((month, idx) => (
                      <div key={idx} className="flex-shrink-0 bg-gray-800/50 rounded-lg p-3 min-w-[120px]">
                        <p className="text-xs text-gray-500 mb-1">{month.monthLabel || month.month}</p>
                        <p className="text-lg font-semibold text-white">{formatCurrency(month.total)}</p>
                        {month.change !== undefined && month.change !== null && (
                          <p className={`text-xs flex items-center gap-1 ${month.change >= 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {month.change >= 0 ? (
                              <ArrowUpRight className="w-3 h-3" />
                            ) : (
                              <ArrowDownRight className="w-3 h-3" />
                            )}
                            {month.change >= 0 ? '+' : ''}{typeof month.change === 'number' ? month.change.toFixed(1) : month.change}%
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Subscriptions Section */}
              {analysis.subscriptions && (
                <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-500/30 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-white flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-cyan-400" />
                      Abonelikler
                    </h3>
                    {/* Summary badges */}
                    <div className="flex items-center gap-2">
                      {(analysis.subscriptions.count || analysis.subscriptions.items?.length || analysis.subscriptions.length) > 0 && (
                        <span className="text-xs px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-full">
                          {analysis.subscriptions.count || analysis.subscriptions.items?.length || analysis.subscriptions.length} abonelik
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Totals */}
                  {(analysis.subscriptions.totalMonthly || analysis.subscriptions.totalYearly) && (
                    <div className="flex gap-4 mb-4 p-3 bg-gray-800/50 rounded-lg">
                      {analysis.subscriptions.totalMonthly > 0 && (
                        <div>
                          <p className="text-xs text-gray-500">Aylık Toplam</p>
                          <p className="text-lg font-semibold text-cyan-400">
                            {formatCurrency(analysis.subscriptions.totalMonthly)}
                          </p>
                        </div>
                      )}
                      {analysis.subscriptions.totalYearly > 0 && (
                        <div>
                          <p className="text-xs text-gray-500">Yıllık Toplam</p>
                          <p className="text-lg font-semibold text-orange-400">
                            {formatCurrency(analysis.subscriptions.totalYearly)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Warning message if exists */}
                  {analysis.subscriptions.warning && (
                    <div className="mb-4 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-xs text-amber-400 flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3" />
                        {analysis.subscriptions.warning}
                      </p>
                    </div>
                  )}

                  {/* Subscription items list - show all */}
                  <div className="space-y-2">
                    {(analysis.subscriptions.items || (Array.isArray(analysis.subscriptions) ? analysis.subscriptions : []))
                      .sort((a, b) => (b.monthlyAmount || b.amount || 0) - (a.monthlyAmount || a.amount || 0))
                      .map((sub, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-gray-800/30 hover:bg-gray-800/50 rounded-lg transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                              <Repeat className="w-4 h-4 text-cyan-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{sub.name}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>{sub.frequency || 'Aylık'}</span>
                                {sub.paymentsDetected && (
                                  <>
                                    <span>•</span>
                                    <span>{sub.paymentsDetected} ödeme tespit edildi</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-white">
                              {formatCurrency(sub.monthlyAmount || sub.amount, sub.currency || 'TRY')}
                              <span className="text-xs text-gray-500 font-normal">/ay</span>
                            </p>
                            {sub.yearlyTotal && (
                              <p className="text-xs text-gray-500">
                                Yıllık: {formatCurrency(sub.yearlyTotal, sub.currency || 'TRY')}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Empty state */}
                  {(!analysis.subscriptions.items || analysis.subscriptions.items.length === 0) &&
                   (!Array.isArray(analysis.subscriptions) || analysis.subscriptions.length === 0) && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Düzenli abonelik tespit edilemedi
                    </p>
                  )}
                </div>
              )}

              {/* Insights */}
              {analysis.insights && analysis.insights.length > 0 && (
                <div className="space-y-2">
                  {analysis.insights.map((insight, idx) => {
                    const config = INSIGHT_COLORS[insight.type] || INSIGHT_COLORS.info;
                    const InsightIcon = config.icon;
                    return (
                      <div
                        key={idx}
                        className={`${config.bg} ${config.border} border rounded-lg p-3 flex items-start gap-3`}
                      >
                        <InsightIcon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
                        <div>
                          <p className="text-white font-medium text-sm">{insight.title}</p>
                          <p className="text-gray-400 text-xs mt-1">{insight.description}</p>
                          {insight.impact && (
                            <p className="text-xs text-gray-500 mt-1">Etki: {insight.impact}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recommendations */}
              {analysis.recommendations && analysis.recommendations.length > 0 && (
                <div className="bg-gray-800/30 rounded-lg p-4">
                  <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pink-400" />
                    Tasarruf Önerileri
                  </h3>
                  <div className="space-y-3">
                    {analysis.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${PRIORITY_COLORS[rec.priority] || PRIORITY_COLORS.medium}`}>
                          {rec.priority === 'high' && 'Yüksek'}
                          {rec.priority === 'medium' && 'Orta'}
                          {rec.priority === 'low' && 'Düşük'}
                        </span>
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{rec.title}</p>
                          <p className="text-gray-400 text-xs mt-1">{rec.description}</p>
                          {rec.potentialSavings && (
                            <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                              <ChevronRight className="w-3 h-3" />
                              Potansiyel tasarruf: {typeof rec.potentialSavings === 'number' ? formatCurrency(rec.potentialSavings) : rec.potentialSavings}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // No analysis yet - prompt to run
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <PieChart className="w-16 h-16 text-gray-600" />
              <p className="text-gray-400 text-center">
                Harcama analizi için veri hazır
              </p>
              <button
                onClick={runSpendingAnalysis}
                className="flex items-center gap-2 px-6 py-3 bg-pink-600 hover:bg-pink-500 text-white rounded-lg transition-colors"
              >
                <Sparkles className="w-5 h-5" />
                Analiz Başlat
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={closeSpendingAnalysis}
            className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
          >
            Kapat
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default SpendingAnalysisModal;
